/*
 * Copyright (c) 2022 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { Asset, AssetType } from '@wit/vtv-x-foundation-lib';
import React, { useState } from 'react';
import Constants from 'configs/Constants';
import Card, { CARDS_DYNAMIC_DIRECTION } from '../Card';
import FillingCard from '../Card/FillingCard';
import { Spinner } from '..';
import RailSlider from './RailSlider';

interface Props {
  railId: number | string;
  menuItemId?: number;
  railName?: string;
  className?: string;
  hasMore?: boolean;
  assets?: Asset[];
  isGrid?: boolean; // DEPRECATED
  channelId?: number;
  showSeriesInfo?: boolean;
  identifyScheduled?: boolean;
  isLoadingAssets?: boolean;
  maxNumberOfAssets?: number;
  onCardClick?: (asset: Asset, menuItemId?: number) => void;
  onHeaderClick?: (railId: number | string, channelId?: number) => void;
}

interface FilledRailProps {
  menuItemId?: number;
  assets?: Asset[];
  isGrid?: boolean;
  maxNumberOfAssets?: number;
  showSeriesInfo?: boolean;
  identifyScheduled?: boolean;
  onCardClick?: (asset: Asset, menuItemId?: number) => void;
  railId: number | string;
  setIsHeaderEnabled: (isEnabled: boolean) => void;
}

const getMaxAssetsNumber = (maxNumberOfAssets?: number, isGrid?: boolean): number => {
  if (!isGrid) {
    return maxNumberOfAssets || Constants.MAX_ASSETS_PER_RAIL;
  }

  return maxNumberOfAssets || Constants.ASSETS_PER_GRID_PAGE;
};

const getFilledRail = ({
  menuItemId,
  assets,
  isGrid,
  maxNumberOfAssets,
  showSeriesInfo,
  identifyScheduled,
  onCardClick,
  railId,
  setIsHeaderEnabled,
}: FilledRailProps): JSX.Element | null => {
  const maxAssetsNumber = getMaxAssetsNumber(maxNumberOfAssets, isGrid);

  if (!assets?.length) {
    const emptyCards = new Array(maxAssetsNumber).map((_el, index) => {
      const key = `filingCard_${index}`;
      return <FillingCard index={index} ctxId={railId} key={key} />;
    });
    return <RailSlider cards={emptyCards} />;
  }

  const handleHeader = (isEnabled: boolean) => {
    setIsHeaderEnabled(isEnabled);
  };

  const railCards = [];

  const wideCards = assets.filter((asset) => asset.type === AssetType.WIDE).length;
  const spacesToFill = maxAssetsNumber - (assets.length % maxAssetsNumber || maxAssetsNumber);

  let assetsToAdd = isGrid ? assets.length + spacesToFill : maxAssetsNumber;
  if (!isGrid && wideCards && wideCards < maxAssetsNumber) {
    assetsToAdd += maxAssetsNumber - (wideCards % maxAssetsNumber) || maxAssetsNumber;
  }

  let portraitOnlyRail = true;

  for (let index = 0; index < assetsToAdd; index += 1) {
    if (index < assets.length && assets[index]) {
      railCards.push(
        <Card
          setIsMouseEnter={(value: boolean) => handleHeader(value)}
          asset={assets[index]}
          onAssetClick={(asset) => onCardClick && onCardClick(asset, menuItemId)}
          showSeriesInfo={showSeriesInfo}
          identifyScheduled={identifyScheduled}
          dynamicDirection={isGrid ? CARDS_DYNAMIC_DIRECTION.HORIZONTAL : CARDS_DYNAMIC_DIRECTION.VERTICAL}
          key={index}
          ctxId={railId}
        />
      );
      portraitOnlyRail = portraitOnlyRail && assets[index].type === AssetType.PORTRAIT;
    }
  }

  return isGrid ? <></> : <RailSlider cards={railCards} large={portraitOnlyRail} />;
};

export default function Rail({
  railId,
  menuItemId,
  railName,
  className = '',
  hasMore,
  assets,
  isGrid, // DEPRECATED
  channelId,
  showSeriesInfo,
  identifyScheduled,
  isLoadingAssets,
  maxNumberOfAssets,
  onCardClick,
  onHeaderClick,
}: Props): JSX.Element {
  const [isHeaderEnabled, setIsHeaderEnabled] = useState(true);

  const headerClicked = () => {
    if (hasMore && onHeaderClick) {
      onHeaderClick(railId, channelId);
    }
  };

  return (
    <div className={`rail ${className}`}>
      <div className={`${isGrid ? 'rail-grid' : 'rail-row'}`} id={`Rail___${railId}`}>
        {railName && (
          <header
            onClick={headerClicked}
            className={`rail-header ${hasMore ? 'has-more' : ''} ${isHeaderEnabled ? 'header-on-top' : ''}`}
            id={`Rail_header___${railId}`}
          >
            <h2>
              <a className={`${!hasMore ? 'disabled-href' : ''}`} id={`Rail_name___${railId}`}>
                {railName}
              </a>
            </h2>
          </header>
        )}

        {!isLoadingAssets ? (
          getFilledRail({
            maxNumberOfAssets,
            menuItemId,
            assets,
            isGrid,
            showSeriesInfo,
            identifyScheduled,
            onCardClick,
            setIsHeaderEnabled,
            railId,
          })
        ) : (
          <div className="loading-rail-assets" id={`Rail_loading___${railId}`}>
            <Spinner isActive />
          </div>
        )}
      </div>
    </div>
  );
}
